import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as lodash from 'lodash';
import { Subscription } from 'rxjs';

import { Project } from '@rappider/rappider-sdk';
import { CardListConfig, ListMode, RappiderCrudViewModule, RappiderTitleToolbarModule, ViewMode } from '@rappider/rappider-components';
import {
  Action, ButtonComponentConfig, CrudFormConfig,
  HeadingComponentConfig, HeadingType
} from '@rappider/rappider-components/utils';

import { selectActiveProject } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { NotificationService } from '@rappider/services';

import {
  CREATE_ITEM_TITLE, EDIT_ITEM_TITLE, CARDS_LIST_CONFIG,
  CREATE_FORM_CONFIG, LIST_CONFIG, LIST_ITEMS_TITLE, MODEL_NAME_SINGULAR
} from './ai-function-call-crud.config';

// #region Component specific values, only change this region to setup a crud component
/* EntityName____ => AiFunctionCall */

import {
  selectAiFunctionCalls as ENTITY_DATA_SELECTOR,
  selectAiFunctionCallLoaded as ENTITY_DATA_LOADED_SELECTOR

} from '@rappider/shared/data-stores';

/* Actions */
import {
  CreateAiFunctionCall as CREATE_ENTITY_ACTION,
  DeleteAiFunctionCall as DELETE_ENTITY_ACTION,
  GetAiFunctionCalls as GET_ENTITIES_ACTION,
  UpdateAiFunctionCall as UPDATE_ENTITY_ACTION
} from '@rappider/shared/data-stores';


import { AiFunctionCall as EntityModelInterface } from '@rappider/rappider-sdk';
import { RappiderAiFunctionExecuteComponent } from '../ai-function-execute/ai-function-execute.component';

/* Component specific values */
export const COMPONENT_SELECTOR = 'rappider-ai-function-call-crud';
export const COMPONENT_HTML_TEMPLATE_PATH = './ai-function-call-crud.component.html';
export const COMPONENT_STYLES_TEMPLATE_PATH = './ai-function-call-crud.component.scss';
export const ENTITY_ORDER_FIELDS = [['name'], ['asc']];

// #endregion

@Component({
  selector: COMPONENT_SELECTOR,
  standalone: true,
  imports: [
    RappiderCrudViewModule,
    RappiderAiFunctionExecuteComponent
  ],
  templateUrl: COMPONENT_HTML_TEMPLATE_PATH,
  styleUrls: [COMPONENT_STYLES_TEMPLATE_PATH]
})
export class RapiderAICrudComponent implements OnInit, OnDestroy {
  viewMode = ViewMode.List;
  ListMode = ListMode;

  /* Crud configs */
  listGridConfig = LIST_CONFIG;
  cardListConfig: CardListConfig = CARDS_LIST_CONFIG;
  createItemFormConfig: CrudFormConfig = CREATE_FORM_CONFIG;
  updateItemFormConfig: CrudFormConfig = CREATE_FORM_CONFIG;
  modelNameSingular = '';
  createItemTitle = '';

  /* data  */
  entities: EntityModelInterface[] | undefined | null;
  entityOrderFields = ENTITY_ORDER_FIELDS;

  /* Active item id in the path for editing or displaying the details of an item */
  activeItemId?: string;
  activeItem?: EntityModelInterface;

  /* active project to get the related data */
  activeProject: Project | undefined;

  /* data loading flag */
  isListGridDataLoading = true;
  /* data loaded */
  isEntityDataLoaded = false;

  /* subscriptions for the data */
  subscriptions: Subscription[] = [];

  /* calculated card subtitles */
  subtitles: { [key: string]: HeadingComponentConfig[] } = {};

  /* config to set title according to crud pages */
  mainTitleConfig: HeadingComponentConfig | undefined;
  mainTitleConfigs: Record<string, HeadingComponentConfig | undefined> = {};

  constructor(
    private store: Store<any>,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) { }

  /* get data and set view mode on init */
  ngOnInit(): void {
    this.getPathParameters();
    this.subscribeToData();
    this.setTranslatedTitles();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /* get path parameters for view mode and editing item id */
  getPathParameters() {
    /* active item id is read from the path in order to show data to edit, e.g. /projects/edit/{id} */
    this.activeItemId = this.route.snapshot?.params?.itemId;
    this.viewMode = this.route.snapshot?.params?.viewMode || ViewMode.List;
    /* set main title after setting the viewMode */
    this.setMainTitle();
  }

  setTranslatedTitles() {
    this.createItemTitle = this.translateService.instant(CREATE_ITEM_TITLE);
    this.modelNameSingular = this.translateService.instant(MODEL_NAME_SINGULAR);
  }

  /* set main title for different crud views */
  setMainTitle() {
    this.mainTitleConfigs = {
      'create': { content: this.translateService.instant(CREATE_ITEM_TITLE), type: HeadingType.H3 },
      'edit': { content: this.translateService.instant(EDIT_ITEM_TITLE), type: HeadingType.H3 },
      'list': { content: this.translateService.instant(LIST_ITEMS_TITLE), type: HeadingType.H3 }
    };
    switch (this.viewMode) {
      // title is create
      case ViewMode.Create:
        this.mainTitleConfig = { content: this.translateService.instant(CREATE_ITEM_TITLE), type: HeadingType.H3 };
        break;
      // title is edit
      case ViewMode.Edit:
        this.mainTitleConfig = { content: this.translateService.instant(EDIT_ITEM_TITLE), type: HeadingType.H3 };
        break;
      // title is list
      default:
        this.mainTitleConfig = { content: this.translateService.instant(LIST_ITEMS_TITLE), type: HeadingType.H3 };
        break;
    }
  }

  /* get data for crud list */
  subscribeToData() {
    this.subscriptions = [
      // get active project
      this.store.select(selectActiveProject).subscribe(activeProject => {
        this.activeProject = activeProject;
      }),
      // get if entities loaded to the store
      this.store.select(ENTITY_DATA_LOADED_SELECTOR).subscribe(isDataLoaded => {
        this.isEntityDataLoaded = !!isDataLoaded;
        if (!isDataLoaded) {
          this.store.dispatch(GET_ENTITIES_ACTION());
        }
      }),
      // get entities data
      this.store.select(ENTITY_DATA_SELECTOR).subscribe(entities => {
        this.entities = entities;

        // set active item if view mode is edit or update
        if (this.activeItemId) {
          this.activeItem = lodash.find(entities, { id: this.activeItemId });
        }
      }
      ),
    ];
  }

  /* on create */
  onCreateFormSubmit(newEntity: EntityModelInterface) {
    console.log(newEntity);
    if (this.activeProject) {
      this.store.dispatch(CREATE_ENTITY_ACTION({
        payload: { newEntity }
      }));
    }
  }

  /* on update */
  onUpdateFormSubmit(updatedData: Partial<EntityModelInterface>) {
    // console.log(updatedData);
    if (
      updatedData && this.activeItem?.id &&
      (this.viewMode === ViewMode.Edit || this.viewMode === ViewMode.Update)
    ) {
      this.store.dispatch(UPDATE_ENTITY_ACTION({
        payload: {
          id: this.activeItem?.id,
          updatedData
        }
      }));
    }
  }

  /* on delete */
  onItemDeleted(deletedItemAction: { action: Action; data: EntityModelInterface }) {
    // console.log(deletedItemAction);
    if (deletedItemAction?.data?.id) {
      this.store.dispatch(DELETE_ENTITY_ACTION({
        payload: { id: deletedItemAction?.data?.id }
      }));
    }
  }

  /* on title bar action */
  onTitleBarActionButtonClicked(action: ButtonComponentConfig) {
    console.log(action);
  }

}
